import {forwardRef} from "react";
import {Input} from "../Input";

const PHONE_PLACEHOLDER = "+7 (___) ___-__-__";

const formatPhoneValue = (value: string) => {
  const digits = value.replace(/\D/g, "");
  const startsWithCountryCode = digits.startsWith("7") || digits.startsWith("8");
  const normalizedDigits = (startsWithCountryCode ? digits.slice(1) : digits).slice(0, 10);

  if (startsWithCountryCode && !normalizedDigits) {
    return "+7";
  }

  if (!normalizedDigits) {
    return "";
  }

  if (normalizedDigits.length <= 3) {
    return `+7 (${normalizedDigits}`;
  }

  if (normalizedDigits.length <= 6) {
    return `+7 (${normalizedDigits.slice(0, 3)}) ${normalizedDigits.slice(3)}`;
  }

  if (normalizedDigits.length <= 8) {
    return `+7 (${normalizedDigits.slice(0, 3)}) ${normalizedDigits.slice(3, 6)}-${normalizedDigits.slice(6)}`;
  }

  return `+7 (${normalizedDigits.slice(0, 3)}) ${normalizedDigits.slice(3, 6)}-${normalizedDigits.slice(6, 8)}-${normalizedDigits.slice(8, 10)}`;
};

type PhoneInputProps = Omit<
  React.ComponentPropsWithoutRef<typeof Input>,
  "autoComplete" | "inputMode" | "placeholder" | "type"
>;

export const PhoneInput = forwardRef<HTMLInputElement, PhoneInputProps>(
  ({defaultValue, onChange, value, ...props}, ref) => {
    const handleChange: React.ChangeEventHandler<HTMLInputElement> = (event) => {
      const formattedValue = formatPhoneValue(event.target.value);

      if (event.target.value !== formattedValue) {
        event.target.value = formattedValue;
      }

      onChange?.(event);
    };

    return (
      <Input
        {...props}
        ref={ref}
        type="tel"
        inputMode="tel"
        autoComplete="tel"
        placeholder={PHONE_PLACEHOLDER}
        defaultValue={
          typeof defaultValue === "string" ? formatPhoneValue(defaultValue) : defaultValue
        }
        value={typeof value === "string" ? formatPhoneValue(value) : value}
        onChange={handleChange}
      />
    );
  },
);

PhoneInput.displayName = "PhoneInput";
